import { Button, ButtonSuffix } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { LeadSaveManager } from "~/react/components/lead-save-manager"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuAction,
  DropdownMenuTrigger,
} from "~/react/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogCloseButton,
  DialogTitle,
} from "~/react/ui/dialog"
import { useRef, useState, useEffect } from "react"
import { type TabSelected } from "~/react/types/tabSelected"

interface BulkActionsToolbarProps {
  className?: string
  selectedIds: number[]
  numberOfResults: number
  numberOfResultsForCurrentTab: number
  alreadyRevealedCount: number
  currentTab?: TabSelected
  removeSelection: () => void
  selectAll: () => void
  selectCurrentTab: () => void
  saveLeads: () => void
  handleLeadManagerSave: () => void
  leadsLists: { id: number; name: string }[]
  revealingIds: number[]
  revealAllOperationCost: number
  currentCredits: number
  exportEmails: () => void
}

export default function BulkActionsToolbar({
  className,
  selectedIds,
  numberOfResults,
  numberOfResultsForCurrentTab,
  alreadyRevealedCount,
  currentTab = "all_people",
  removeSelection,
  selectAll,
  selectCurrentTab,
  saveLeads,
  handleLeadManagerSave,
  leadsLists,
  revealingIds,
  revealAllOperationCost,
  currentCredits,
  exportEmails,
}: BulkActionsToolbarProps) {
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const exportDialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (exportDialogOpen) {
      exportDialogRef.current?.showModal()
    } else {
      exportDialogRef.current?.close()
    }
  }, [exportDialogOpen, exportDialogRef])

  const difference = selectedIds.length - currentCredits

  return (
    <>
      <div className={`ds-bulk-actions ${className}`}>
        {/* Close button */}
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={removeSelection}
          title="Clear selection"
        >
          <Icon icon="times" aria-hidden="true" />
        </Button>

        {/* Selection dropdown - matches original structure */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm">
              <span>{selectedIds.length}</span>
              {" selected"}
              <span className="far fa-angle-down" aria-hidden="true"></span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            size={currentTab === "decision_makers" ? "md" : undefined}
          >
            <li>
              <DropdownMenuAction onClick={selectAll}>
                {i18n.t("js.search.bulk_actions_toolbar.select_all")}
                <small className="grey ml-auto">({numberOfResults})</small>
              </DropdownMenuAction>
            </li>
            <li>
              <DropdownMenuAction onClick={selectCurrentTab}>
                {currentTab === "all_people"
                  ? i18n.t("js.search.bulk_actions_toolbar.select_all_people")
                  : currentTab === "decision_makers"
                    ? i18n.t(
                        "js.search.bulk_actions_toolbar.select_all_decision_makers",
                      )
                    : i18n.t(
                        "js.search.bulk_actions_toolbar.select_all_generic",
                      )}
                <small className="grey ml-auto">
                  ({numberOfResultsForCurrentTab})
                </small>
              </DropdownMenuAction>
            </li>
            <li>
              <DropdownMenuAction onClick={removeSelection}>
                {i18n.t("js.search.bulk_actions_toolbar.unselect_all")}
              </DropdownMenuAction>
            </li>
          </DropdownMenuContent>
        </DropdownMenu>

        <hr className="h-table-bulks-actions__separator" />

        {/* Reveal & Save dropdown - matches Lead::BulkSaveDropdownComponent */}
        <div className="d-flex">
          <div className="dropdown">
            <Button
              variant="ghost"
              size="sm"
              disabled={revealingIds.length > 0}
              onClick={() => saveLeads(null)}
            >
              {i18n.t("js.search.bulk_actions_toolbar.reveal_email_addresses")}
              <ButtonSuffix>
                <span className="cost-indicator cost-indicator--toolbar">
                  <Icon
                    icon="coin"
                    size="xs"
                    className="cost-indicator__icon"
                  />
                  {revealAllOperationCost}
                </span>
              </ButtonSuffix>
            </Button>
            <Button
              size="sm"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              disabled={revealingIds.length > 0}
            >
              <Icon icon="angle-down" aria-hidden="true" />
            </Button>

            {/* Lead Save Manager Dropdown */}
            <LeadSaveManager
              id="domain-search-bulk"
              leadsLists={leadsLists}
              onSave={handleLeadManagerSave}
              disableButtons={revealingIds.length > 0}
              cost={revealAllOperationCost}
              hint={
                alreadyRevealedCount > 0
                  ? i18n.t(
                      "js.multi_domain_search.footer.existing_leads_hint",
                      { count: alreadyRevealedCount },
                    )
                  : undefined
              }
            >
              <div></div>
            </LeadSaveManager>
          </div>
        </div>

        <hr className="h-table-bulks-actions__separator" />

        {/* Export button */}
        <Button
          variant="ghost"
          size="sm"
          disabled={revealingIds.length > 0}
          onClick={() => setExportDialogOpen(true)}
        >
          {i18n.t("js.search.bulk_actions_toolbar.export")}
          <div className="h-button__suffix">
            <span className="cost-indicator cost-indicator--toolbar">
              <Icon icon="coin" size="xs" className="cost-indicator__icon" />
              {revealAllOperationCost}
            </span>
          </div>
        </Button>
      </div>

      {/* Export dialog */}
      <Dialog ref={exportDialogRef}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {i18n.t(
                "js.search.bulk_actions_toolbar.export_confirmation_dialog.title",
              )}
            </DialogTitle>
            <DialogCloseButton onClick={() => setExportDialogOpen(false)} />
          </DialogHeader>
          <DialogBody>
            {difference > 0 ? (
              <p>
                {i18n.t(
                  "js.search.bulk_actions_toolbar.export_confirmation_dialog.lack_of_credits_html",
                  {
                    numberOfResults: selectedIds.length,
                    currentCredits,
                  },
                )}{" "}
                <a
                  href="/welcome/upgrade?from=domain_search"
                  data-turbo-frame="_top"
                >
                  {i18n.t(
                    "js.search.bulk_actions_toolbar.export_confirmation_dialog.upgrade_plan",
                  )}
                </a>{" "}
                {i18n.t(
                  "js.search.bulk_actions_toolbar.export_confirmation_dialog.to_export_all_your_selection",
                )}
              </p>
            ) : (
              <p>
                {i18n.t(
                  "js.search.bulk_actions_toolbar.export_confirmation_dialog.description_html",
                  { credits: revealAllOperationCost },
                )}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setExportDialogOpen(false)}>
              {i18n.t(
                "js.search.bulk_actions_toolbar.export_confirmation_dialog.cancel",
              )}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                exportEmails()
                setExportDialogOpen(false)
              }}
            >
              {i18n.t(
                "js.search.bulk_actions_toolbar.export_confirmation_dialog.accept",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
